import { useMemo } from 'react';
import { NEW_CANDIDATE_ENDPOINTS, SOURCES } from 'features/new-candidate/constants';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import queryString from 'query-string';
import API, { SECONDARY_API_CLIENT } from 'services/API';
import { ContactIncludes, HiringAuthorityIncludes } from 'services/includes';
import { Endpoints } from 'UI/constants/endpoints';
import { UIStatus } from 'UI/constants/status';

const DEFAULT_PARAMS = {
  apiInstance: null,
  apiVersion: null,
  endpoint: null
};

const PARAMS_MAP = {
  [SOURCES.FROM_TALENT_POOL]: {
    apiInstance: SECONDARY_API_CLIENT,
    apiVersion: 1,
    endpointTemplate: Endpoints.CandidateDataFromApplicant
  },
  [SOURCES.FROM_RESUME]: {
    apiInstance: SECONDARY_API_CLIENT,
    apiVersion: 1,
    endpointTemplate: Endpoints.CandidateDataFromResumeParser
  }
};

const buildParams = (fromSource, loaderParams) => {
  const id = loaderParams?.id;
  if (!id) return DEFAULT_PARAMS;

  if (loaderParams?.isFromDraft)
    return {
      apiInstance: SECONDARY_API_CLIENT,
      apiVersion: 1,
      endpoint: NEW_CANDIDATE_ENDPOINTS.draft.replace(':id', id)
    };

  if (!fromSource) return DEFAULT_PARAMS;

  if (fromSource === SOURCES.FROM_DIRECTORY) {
    const { contactId } = loaderParams;
    const isFromContact = Boolean(contactId);

    const baseUrl = isFromContact ? Endpoints.Names : Endpoints.HiringAuthorities;
    const includes = `${isFromContact ? ContactIncludes : HiringAuthorityIncludes},keywordTags`;

    const endpoint = queryString.stringifyUrl({
      url: `${baseUrl}/${id}`,
      query: { includes }
    });

    return {
      apiInstance: API,
      apiVersion: 2,
      endpoint
    };
  }

  const params = PARAMS_MAP[fromSource];
  if (!params) return DEFAULT_PARAMS;

  const { endpointTemplate, ...rest } = params;

  return {
    ...rest,
    endpoint: String(endpointTemplate).replace(':id', String(id))
  };
};

export function useFetchData({ fromSource, loaderParams }) {
  const { apiInstance, apiVersion, endpoint } = useMemo(
    () => buildParams(fromSource, loaderParams),
    [fromSource, loaderParams]
  );

  const { state } = useFetchWithStatusV2({
    endpoint,
    apiInstance,
    apiVersion,
    initialStatus: endpoint ? UIStatus.Loading : UIStatus.Default
  });

  return state;
}
